import React, { useEffect, useState } from 'react';
import { apiFetch } from './services/apiClient';
import './ApplicationTrackingPage.css';

/**
 * Public application tracking UI. Accepts token directly (tests) or via page wrapper.
 */
const ApplicationTrackingView = ({ token }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await apiFetch(`/api/public/applications/track/${token}`);
                if (!response.ok) {
                    if (!cancelled) {
                        setError('Application not found or link has expired.');
                    }
                    return;
                }
                const json = await response.json();
                if (!cancelled) {
                    setData(json);
                }
            } catch (err) {
                if (!cancelled) {
                    setError('Unable to load application status. Please try again later.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        if (token) {
            load();
        } else {
            setError('Invalid tracking link.');
            setLoading(false);
        }
        return () => {
            cancelled = true;
        };
    }, [token]);

    const stages = data?.stages || [];

    return (
        <div className="application-tracking-page">
            <div className="application-tracking-card">
                {loading && <p className="tracking-loading">Loading your application status…</p>}

                {!loading && error && (
                    <>
                        <h1>Application tracking</h1>
                        <p className="tracking-error">{error}</p>
                    </>
                )}

                {!loading && data && (
                    <>
                        <p className="tracking-status-badge" aria-live="polite">
                            Current status: <strong>{data.status_label || data.application_status}</strong>
                        </p>

                        <p className="tracking-intro">
                            You recently applied for{' '}
                            <strong>{data.job_title}</strong> at{' '}
                            <strong>{data.company_name}</strong> through TalentVibe application tracking.
                        </p>

                        <div className="tracking-pipeline" role="list" aria-label="Application progress">
                            {stages.map((stage, index) => {
                                const prev = index > 0 ? stages[index - 1] : null;
                                const connectorFilled = Boolean(prev?.completed);
                                const connectorToCurrent = Boolean(stage.current || stage.completed || stage.rejected);
                                return (
                                    <React.Fragment key={stage.key}>
                                        {index > 0 && (
                                            <div
                                                className={[
                                                    'tracking-connector',
                                                    connectorFilled ? 'filled' : '',
                                                    connectorToCurrent ? 'reaches-next' : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                                aria-hidden="true"
                                            />
                                        )}
                                        <div
                                            role="listitem"
                                            className={[
                                                'tracking-stage',
                                                stage.completed ? 'completed' : '',
                                                stage.current ? 'current' : '',
                                                stage.rejected ? 'rejected' : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                        >
                                            <div className="tracking-stage-icon" aria-hidden="true">
                                                {stage.rejected ? (
                                                    '✕'
                                                ) : stage.completed ? (
                                                    '✓'
                                                ) : stage.current ? (
                                                    <span className="tracking-stage-dot" />
                                                ) : (
                                                    <span className="tracking-stage-empty" />
                                                )}
                                            </div>
                                            <span className="tracking-stage-label">{stage.label}</span>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        <p className="tracking-message">{data.message}</p>

                        {data.rejected && (
                            <p className="tracking-note">
                                This application is closed. Thank you for your interest.
                            </p>
                        )}

                        {data.application_status === 'offer' && (
                            <p className="tracking-note success">
                                You have received an offer for this role. Our team will share next steps soon.
                            </p>
                        )}

                        {data.application_status === 'hired' && (
                            <p className="tracking-note success">
                                Congratulations — you have been selected for this role.
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ApplicationTrackingView;
